/**
 * chatModeMemory — where the chat's mode is remembered, and who may change the
 * person's default (Miles, 2026-10-15).
 *
 * THE ASK. "When a link is provided in chat or in the footer or anywhere in the
 * platform when the user opens that link it takes the user to the full screen
 * chat interface even though it should auto take the user in a new tab to open
 * on that page with the chat minimized in the bottom right of the screen. It
 * keeps the default the user prefers but a link Prest0n or otherwise provides
 * needs to default take the user to the page the user clicked towards without
 * the expanded chat interface in the way."
 *
 * WHY IT HAPPENED. The mode (open, full screen, where a closed chat rests) was
 * ONE memory per browser (localStorage), written on every change by whichever
 * tab made it. A new tab, a link's tab included, booted in whatever some tab had
 * saved last, often full screen; and the page stepping aside for a followed link
 * wrote its own layout over the person's choice.
 *
 * TWO MEMORIES.
 *   · the DEVICE's default: the person's own choice, in the localStorage keys
 *     every new tab starts from (types.ts GLOBAL_CHAT_*);
 *   · this TAB's: what this tab shows now (sessionStorage), read first, so a
 *     reload and the update banner keep it, and no sibling tab can move it.
 * Only the person's own gestures (opening from the bar or the launcher, sending,
 * attaching, docking, full screen, minimizing) change the device's default. All
 * else that moves the chat changes this tab only: the page stepping aside for a
 * followed link, a programmatic open (a thread's deep link, the voice pulling up
 * the thread it is working in), and the restore itself.
 * A tab opened from a link (LINK_LANDING_PARAM, src/lib/linkLanding.ts) starts
 * with the chat closed, resting at the bottom right, in this tab only.
 *
 * This file is the ONE owner of the tab memory's shape (its key, CHAT_MODE_TAB_KEY,
 * sits with the other mode keys in types.ts). Leaf only: the
 * storage helpers and the keys, never the engine, so the boot read
 * (bootChatMode.ts) and src/index.tsx can use it from the first download
 * (scripts/check-chat-first-paint.mjs).
 */
import {
  CHAT_MODE_TAB_KEY,
  GLOBAL_CHAT_DOCK_KEY,
  GLOBAL_CHAT_FULLSCREEN_KEY,
  GLOBAL_CHAT_MINIMIZED_SPOT_KEY,
  GLOBAL_CHAT_OPEN_KEY,
} from './types';
import { getFromLocalStorage, setToLocalStorage } from './utils';
import { parseMinimizedSpot, peekMinimizedSpot } from './chatModeRules';
import type { ChatMinimizedSpot } from './chatModeRules';

/** What a tab remembers: open, full screen, and where a closed chat rests on a desktop. */
export interface ChatModeMemory {
  open: boolean;
  fullScreen: boolean;
  spot: ChatMinimizedSpot;
}

/**
 * How long a gesture's mark stays readable: the persist effect runs one commit
 * after the handler that marked it, well inside this.
 */
export const MODE_GESTURE_WINDOW_MS = 1000;

function session(): Storage | null {
  try {
    return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/** PURE. A stored value, read as a tab memory; anything else (corrupt, partial, foreign) is no memory at all. */
export function parseTabMode(raw: unknown): ChatModeMemory | null {
  if (typeof raw !== 'string' || !raw) return null;
  try {
    const v = JSON.parse(raw) as Record<string, unknown>;
    if (!v || typeof v !== 'object') return null;
    if (typeof v.open !== 'boolean' || typeof v.fullScreen !== 'boolean') return null;
    return { open: v.open, fullScreen: v.fullScreen, spot: parseMinimizedSpot(v.spot) };
  } catch {
    return null;
  }
}

/** This tab's memory, or null when this tab has none yet. */
export function readTabMode(): ChatModeMemory | null {
  const s = session();
  if (!s) return null;
  try {
    return parseTabMode(s.getItem(CHAT_MODE_TAB_KEY));
  } catch {
    return null;
  }
}

/** The device's default: the person's own last choice. Reading it records nothing. */
export function readDeviceMode(): ChatModeMemory {
  if (typeof window === 'undefined') return { open: false, fullScreen: false, spot: 'center' };
  return {
    open: getFromLocalStorage<string>(GLOBAL_CHAT_OPEN_KEY, '0') === '1',
    fullScreen: getFromLocalStorage<string>(GLOBAL_CHAT_FULLSCREEN_KEY, '0') === '1',
    spot: peekMinimizedSpot(),
  };
}

/** What this tab should show: its own memory, else the device's default. */
export function readChatMode(): ChatModeMemory {
  return readTabMode() ?? readDeviceMode();
}

/** Record what this tab shows now (never the device's default). */
export function writeTabMode(next: Partial<ChatModeMemory>): void {
  const s = session();
  if (!s) return;
  const base = readChatMode();
  const merged: ChatModeMemory = {
    open: typeof next.open === 'boolean' ? next.open : base.open,
    fullScreen: typeof next.fullScreen === 'boolean' ? next.fullScreen : base.fullScreen,
    spot: next.spot ? parseMinimizedSpot(next.spot) : base.spot,
  };
  try {
    s.setItem(CHAT_MODE_TAB_KEY, JSON.stringify(merged));
  } catch {
    // a full or blocked session store: this tab simply has no memory of its own
  }
}

/**
 * Record the person's own choice: the device's default (what a new tab starts
 * from) and this tab's memory. `docked` is kept for the readers of the dock key
 * (on a desktop it is derived: docked exactly when not full screen).
 */
export function writeDeviceMode(next: Partial<ChatModeMemory> & { docked?: boolean }): void {
  if (typeof window === 'undefined') return;
  if (typeof next.open === 'boolean') setToLocalStorage(GLOBAL_CHAT_OPEN_KEY, next.open ? '1' : '0');
  if (typeof next.fullScreen === 'boolean') setToLocalStorage(GLOBAL_CHAT_FULLSCREEN_KEY, next.fullScreen ? '1' : '0');
  if (typeof next.docked === 'boolean') setToLocalStorage(GLOBAL_CHAT_DOCK_KEY, next.docked ? '1' : '0');
  if (next.spot) setToLocalStorage(GLOBAL_CHAT_MINIMIZED_SPOT_KEY, parseMinimizedSpot(next.spot));
  writeTabMode(next);
}

/** Forget this tab's memory (the first arrival lands on the page from the device's default). */
export function clearTabMode(): void {
  const s = session();
  if (!s) return;
  try {
    s.removeItem(CHAT_MODE_TAB_KEY);
  } catch {
    // nothing to forget
  }
}
